import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, TextField, MenuItem } from '@mui/material';
import { FindLoad, FindSuccessItem, Images } from 'utils';
import { CreateChancel, CustomizedSwitch, SimpleTooltip } from 'components';
import { fundingSourceActions, httpRequestsOnSuccessActions } from 'store';
import { SupervisionPeriod } from './supervisionConstants';
import { CMS1500Box1, CMS1500Box1Labels, CMS1500Box24EPSDT, CMS1500Box24EPSDTLabels } from './cms1500Constants';

const ACTION_TYPE = 'EDIT_FS_SOURCE_GROUPING';
const CMS1500_ACTION_TYPE = 'EDIT_CMS1500_SETTINGS';
const SUPERVISION_ACTION_TYPE = 'EDIT_SUPERVISION_REQUIREMENT';

export const BillingSettings = () => {
    const fsSource = useSelector((state) => state.fundingSource.fundingSourceItem);
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const successMerging = FindSuccessItem(ACTION_TYPE);
    const supervisionLoader = FindLoad(SUPERVISION_ACTION_TYPE);
    const supervisionSuccess = FindSuccessItem(SUPERVISION_ACTION_TYPE);
    const cms1500Loader = FindLoad(CMS1500_ACTION_TYPE);
    const cms1500Success = FindSuccessItem(CMS1500_ACTION_TYPE);
    const [showEdit, setShowEdit] = useState(false);
    const [showSupervisionEdit, setShowSupervisionEdit] = useState(false);
    const [showCms1500Edit, setShowCms1500Edit] = useState(false);
    const checkEditState = showEdit;
    const disabled = !showEdit;
    const supervisionDisabled = !showSupervisionEdit;
    const cms1500Disabled = !showCms1500Edit;
    const [groupOptions, setGroupOptions] = useState({
        byCPTCode: false,
        byPOS: false,
        byStaff: false,
        mergeBilling: false,
        mergeBillingPlaceService: true,
        invoiceOnly: false,
    });
    const [supervisionOptions, setSupervisionOptions] = useState({
        enabled: false,
        minPercent: 0,
        period: SupervisionPeriod.MONTHLY,
    });
    const [cms1500Options, setCms1500Options] = useState({
        box1: CMS1500Box1.GROUP_HEALTH_PLAN,
        box24EPSDT: CMS1500Box24EPSDT.EMPTY,
        useStaffTaxonomyCode: false,
        useStaffNpi: false,
    });
    // per-line staff NPI needs one staff per line, so merging stays off while it is on
    const staffNpiOn = !!fsSource?.cms1500Settings?.useStaffNpi;

    useEffect(() => {
        if (fsSource?.id) {
            setGroupOptions({
                byCPTCode: fsSource?.billingSettings?.byCPTCode,
                byPOS: fsSource?.billingSettings?.byPOS,
                byStaff: fsSource?.billingSettings?.byStaff,
                mergeBilling: fsSource?.billingSettings?.mergeBilling,
                mergeBillingPlaceService: fsSource?.billingSettings?.mergeBillingPlaceService !== false,
                invoiceOnly: fsSource?.billingSettings?.invoiceOnly,
            });
            setSupervisionOptions({
                enabled: fsSource?.supervisionRequirement?.enabled || false,
                minPercent: fsSource?.supervisionRequirement?.minPercent || 0,
                period: fsSource?.supervisionRequirement?.period || SupervisionPeriod.MONTHLY,
            });
            setCms1500Options({
                box1: fsSource?.cms1500Settings?.box1 || CMS1500Box1.GROUP_HEALTH_PLAN,
                box24EPSDT: fsSource?.cms1500Settings?.box24EPSDT ?? CMS1500Box24EPSDT.EMPTY,
                useStaffTaxonomyCode: fsSource?.cms1500Settings?.useStaffTaxonomyCode || false,
                useStaffNpi: fsSource?.cms1500Settings?.useStaffNpi || false,
            });
        }
    }, [fsSource]);

    useEffect(() => {
        if (successMerging?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            setShowEdit(false);
        }
    }, [successMerging]);

    useEffect(() => {
        if (supervisionSuccess?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(SUPERVISION_ACTION_TYPE));
            setShowSupervisionEdit(false);
        }
    }, [supervisionSuccess]);

    useEffect(() => {
        if (cms1500Success?.type) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(CMS1500_ACTION_TYPE));
            setShowCms1500Edit(false);
        }
    }, [cms1500Success]);

    const handleSubmit = () => {
        if (
            fsSource?.billingSettings?.byCPTCode !== groupOptions.byCPTCode ||
            fsSource?.billingSettings?.byPOS !== groupOptions.byPOS ||
            fsSource?.billingSettings?.byStaff !== groupOptions.byStaff ||
            fsSource?.billingSettings?.mergeBilling !== groupOptions?.mergeBilling ||
            fsSource?.billingSettings?.mergeBillingPlaceService !== groupOptions?.mergeBillingPlaceService ||
            fsSource?.billingSettings?.invoiceOnly !== groupOptions?.invoiceOnly
        ) {
            dispatch(fundingSourceActions.editFsSourceGrouping(fsSource?.id, groupOptions));
        }
    };

    const handleCancel = () => {
        setShowEdit(false);
    };

    const handleCheck = (e) => {
        setGroupOptions((prevState) => ({ ...prevState, [e.target.name]: e.target.checked }));
    };

    const handleSupervisionSubmit = () => {
        dispatch(
            fundingSourceActions.editSupervisionRequirement(fsSource?.id, {
                supervisionRequirement: supervisionOptions,
            })
        );
    };

    const handleSupervisionCancel = () => {
        setShowSupervisionEdit(false);
        setSupervisionOptions({
            enabled: fsSource?.supervisionRequirement?.enabled || false,
            minPercent: fsSource?.supervisionRequirement?.minPercent || 0,
            period: fsSource?.supervisionRequirement?.period || SupervisionPeriod.MONTHLY,
        });
    };

    const handleCms1500Submit = () => {
        dispatch(fundingSourceActions.editCms1500Settings(fsSource?.id, cms1500Options));
    };

    const handleCms1500Cancel = () => {
        setShowCms1500Edit(false);
        setCms1500Options({
            box1: fsSource?.cms1500Settings?.box1 || CMS1500Box1.GROUP_HEALTH_PLAN,
            box24EPSDT: fsSource?.cms1500Settings?.box24EPSDT ?? CMS1500Box24EPSDT.EMPTY,
            useStaffTaxonomyCode: fsSource?.cms1500Settings?.useStaffTaxonomyCode || false,
            useStaffNpi: fsSource?.cms1500Settings?.useStaffNpi || false,
        });
    };

    return (
        <div className="billing-settings-wrapper">
            {/* Billing Setup + Supervision — Left */}
            <div className="billing-settings-box">
                <div className="billing-settings-section-header">
                    <p className="billing-settings-title">Billing Setup</p>
                    {!checkEditState && (
                        <button id={'billing-setup-edit'} className="reset-btn-no-paddings" onClick={() => setShowEdit(true)}>
                            <img src={Images.edit} alt="edit" />
                        </button>
                    )}
                </div>

                <div className="billing-settings-body">
                    <div className="billing-settings-switch-row billing-settings-switch-row--column">
                        <div className="billing-settings-switch-content">
                            <SimpleTooltip
                                title={staffNpiOn ? 'Off while Use Staff NPI is on' : 'Click Edit to make changes'}
                                placement="top"
                                arrow>
                                <div
                                    className={`billing-settings-switch-box${
                                        disabled || staffNpiOn ? ' billing-settings-switch-box--disabled' : ''
                                    }`}>
                                    <CustomizedSwitch
                                        disabled={disabled || staffNpiOn}
                                        checked={groupOptions.mergeBilling}
                                        name="mergeBilling"
                                        handleClick={() =>
                                            handleCheck({
                                                target: {
                                                    name: 'mergeBilling',
                                                    checked: !groupOptions.mergeBilling,
                                                },
                                            })
                                        }
                                    />
                                </div>
                            </SimpleTooltip>
                            <div className="billing-settings-switch-label">
                                <p>Merge Appointments</p>
                                <span className="billing-settings-info-text">
                                    {staffNpiOn
                                        ? 'Off while Use Staff NPI is on'
                                        : 'Combines multiple appointments into a single billable line on claims'}
                                </span>
                            </div>
                        </div>

                        {groupOptions.mergeBilling && (
                            <div className="billing-settings-merge-options">
                                <p className="billing-settings-info-text billing-settings-info-text--bold">Group appointments by:</p>
                                <div className="billing-settings-merge-option billing-settings-merge-option--always">
                                    <Checkbox checked={true} disabled={true} className="custom-check-box" />
                                    <span className="billing-settings-info-text">Charge Rate</span>
                                </div>
                                <div className="billing-settings-merge-option billing-settings-merge-option--always">
                                    <Checkbox checked={true} disabled={true} className="custom-check-box" />
                                    <span className="billing-settings-info-text">Start Date</span>
                                </div>
                                <div
                                    className={`billing-settings-merge-option${
                                        disabled ? ' billing-settings-merge-option--disabled' : ''
                                    }`}>
                                    <Checkbox
                                        disabled={disabled}
                                        checked={groupOptions.mergeBillingPlaceService}
                                        name="mergeBillingPlaceService"
                                        className="custom-check-box"
                                        onChange={handleCheck}
                                    />
                                    <span className="billing-settings-info-text">Place of Service</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="billing-settings-switch-row">
                        <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                            <div className={`billing-settings-switch-box${disabled ? ' billing-settings-switch-box--disabled' : ''}`}>
                                <CustomizedSwitch
                                    disabled={disabled}
                                    checked={groupOptions.invoiceOnly}
                                    name="invoiceOnly"
                                    handleClick={() =>
                                        handleCheck({
                                            target: {
                                                name: 'invoiceOnly',
                                                checked: !groupOptions.invoiceOnly,
                                            },
                                        })
                                    }
                                />
                            </div>
                        </SimpleTooltip>
                        <div className="billing-settings-switch-label">
                            <p>Invoice Only</p>
                            <span className="billing-settings-info-text">
                                If enabled, this funding source will generate invoices directly without creating claims
                            </span>
                        </div>
                    </div>

                    <div className="billing-settings-claim-grouping">
                        <p className="billing-settings-info-text billing-settings-info-text--bold">Claim Grouping Options</p>
                        <p className="billing-settings-info-text">Select how claims should be grouped during generation.</p>
                        <div className="billing-settings-grouping-options">
                            <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                                <div
                                    className={`billing-settings-grouping-option${
                                        groupOptions.byPOS ? ' billing-settings-grouping-option--active' : ''
                                    }`}>
                                    <Checkbox
                                        className={`custom-check-box${disabled ? ' billing-settings-input--disabled' : ''}`}
                                        disabled={disabled}
                                        checked={groupOptions.byPOS}
                                        name="byPOS"
                                        onChange={handleCheck}
                                    />
                                    <p>By Place of Service</p>
                                </div>
                            </SimpleTooltip>
                            <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                                <div
                                    className={`billing-settings-grouping-option${
                                        groupOptions.byCPTCode ? ' billing-settings-grouping-option--active' : ''
                                    }`}>
                                    <Checkbox
                                        className={`custom-check-box${disabled ? ' billing-settings-input--disabled' : ''}`}
                                        disabled={disabled}
                                        checked={groupOptions.byCPTCode}
                                        name="byCPTCode"
                                        onChange={handleCheck}
                                    />
                                    <p>By CPT Code</p>
                                </div>
                            </SimpleTooltip>
                            <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                                <div
                                    className={`billing-settings-grouping-option${
                                        groupOptions.byStaff ? ' billing-settings-grouping-option--active' : ''
                                    }`}>
                                    <Checkbox
                                        className={`custom-check-box${disabled ? ' billing-settings-input--disabled' : ''}`}
                                        disabled={disabled}
                                        checked={groupOptions.byStaff}
                                        name="byStaff"
                                        onChange={handleCheck}
                                    />
                                    <p>By Staff (RBT)</p>
                                </div>
                            </SimpleTooltip>
                        </div>
                    </div>
                    <div className="billing-settings-tool-info">
                        <img src={Images.toolInfoIcon} alt={'icon'} />
                        <p>Your selected grouping options will be applied during claims generation</p>
                    </div>
                </div>

                <div className="billing-settings-actions">
                    {checkEditState && (
                        <CreateChancel
                            loader={!!loader?.length}
                            create={'Save'}
                            chancel={'Cancel'}
                            onCreate={handleSubmit}
                            onClose={handleCancel}
                            buttonWidth="292px"
                            id={'billing-setup-save'}
                        />
                    )}
                </div>

                <div className="billing-settings-supervision-section">
                    <div className="billing-settings-section-header">
                        <p className="billing-settings-title">Supervision Requirements</p>
                        {!showSupervisionEdit && (
                            <button id={'supervision-edit'} className="reset-btn-no-paddings" onClick={() => setShowSupervisionEdit(true)}>
                                <img src={Images.edit} alt="edit" />
                            </button>
                        )}
                    </div>

                    <div className="billing-settings-body">
                        <div className="billing-settings-switch-row billing-settings-switch-row--column">
                            <div className="billing-settings-switch-content">
                                <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                                    <div
                                        className={`billing-settings-switch-box${
                                            supervisionDisabled ? ' billing-settings-switch-box--disabled' : ''
                                        }`}>
                                        <CustomizedSwitch
                                            disabled={supervisionDisabled}
                                            checked={supervisionOptions.enabled}
                                            name="enabled"
                                            handleClick={() =>
                                                setSupervisionOptions((prev) => ({
                                                    ...prev,
                                                    enabled: !prev.enabled,
                                                }))
                                            }
                                        />
                                    </div>
                                </SimpleTooltip>
                                <div className="billing-settings-switch-label">
                                    <p>Enable Supervision Tracking</p>
                                    <span className="billing-settings-info-text">
                                        When enabled, the system will track supervision compliance for clients using this payer
                                    </span>
                                </div>
                            </div>

                            {supervisionOptions.enabled && (
                                <div className="billing-settings-supervision-fields">
                                    <div>
                                        <p className="billing-settings-info-text billing-settings-info-text--bold-mb8">
                                            Minimum Supervision Percentage
                                        </p>
                                        <TextField
                                            disabled={supervisionDisabled}
                                            type="number"
                                            size="small"
                                            value={supervisionOptions.minPercent}
                                            onChange={(e) => {
                                                const val = Math.max(0, Math.min(100, Number(e.target.value)));
                                                setSupervisionOptions((prev) => ({ ...prev, minPercent: val }));
                                            }}
                                            inputProps={{ min: 0, max: 100, step: 1 }}
                                            className={`billing-settings-input${
                                                supervisionDisabled ? ' billing-settings-input--disabled' : ''
                                            }`}
                                            style={{ width: '120px' }}
                                            InputProps={{
                                                endAdornment: <span style={{ color: '#475467', fontSize: 14 }}>%</span>,
                                            }}
                                        />
                                    </div>

                                    <div>
                                        <p className="billing-settings-info-text billing-settings-info-text--bold-mb8">
                                            Supervision Period
                                        </p>
                                        <TextField
                                            disabled={supervisionDisabled}
                                            select
                                            size="small"
                                            value={supervisionOptions.period}
                                            onChange={(e) => setSupervisionOptions((prev) => ({ ...prev, period: e.target.value }))}
                                            className={`billing-settings-input${
                                                supervisionDisabled ? ' billing-settings-input--disabled' : ''
                                            }`}
                                            style={{ width: '200px' }}>
                                            <MenuItem value={SupervisionPeriod.WEEKLY}>Weekly</MenuItem>
                                            <MenuItem value={SupervisionPeriod.MONTHLY}>Monthly</MenuItem>
                                        </TextField>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="billing-settings-tool-info billing-settings-supervision-info">
                            <img src={Images.toolInfoIcon} alt={'icon'} />
                            <p>Supervision requirements determine the minimum ratio of supervised to direct service hours</p>
                        </div>
                    </div>

                    <div className="billing-settings-actions">
                        {showSupervisionEdit && (
                            <CreateChancel
                                loader={!!supervisionLoader?.length}
                                create={'Save'}
                                chancel={'Cancel'}
                                onCreate={handleSupervisionSubmit}
                                onClose={handleSupervisionCancel}
                                buttonWidth="292px"
                                id={'supervision-save'}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* CMS 1500 Settings Card — Right */}
            <div className="billing-settings-box">
                <div className="billing-settings-section-header">
                    <p className="billing-settings-title">CMS 1500 Settings</p>
                    {!showCms1500Edit && (
                        <button id={'cms1500-edit'} className="reset-btn-no-paddings" onClick={() => setShowCms1500Edit(true)}>
                            <img src={Images.edit} alt="edit" />
                        </button>
                    )}
                </div>

                <div className="billing-settings-body">
                    <div className="billing-settings-cms-fields">
                        <div>
                            <p className="billing-settings-info-text billing-settings-info-text--bold-mb8">Box 1 - Insurance Type</p>
                            <TextField
                                disabled={cms1500Disabled}
                                select
                                size="small"
                                value={cms1500Options.box1}
                                onChange={(e) => setCms1500Options((prev) => ({ ...prev, box1: e.target.value }))}
                                className={`billing-settings-input${cms1500Disabled ? ' billing-settings-input--disabled' : ''}`}
                                style={{ width: '100%' }}>
                                {Object.values(CMS1500Box1).map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {CMS1500Box1Labels[value]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>

                        <div>
                            <p className="billing-settings-info-text billing-settings-info-text--bold-mb8">
                                Box 24-H - EPSDT / Family Plan
                            </p>
                            <TextField
                                disabled={cms1500Disabled}
                                select
                                size="small"
                                value={cms1500Options.box24EPSDT}
                                onChange={(e) => setCms1500Options((prev) => ({ ...prev, box24EPSDT: e.target.value }))}
                                className={`billing-settings-input${cms1500Disabled ? ' billing-settings-input--disabled' : ''}`}
                                style={{ width: '100%' }}>
                                {Object.values(CMS1500Box24EPSDT).map((value) => (
                                    <MenuItem key={value || 'empty'} value={value}>
                                        {CMS1500Box24EPSDTLabels[value]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    </div>

                    <div className="billing-settings-switch-row" style={{ marginTop: 16 }}>
                        <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                            <div
                                className={`billing-settings-switch-box${cms1500Disabled ? ' billing-settings-switch-box--disabled' : ''}`}>
                                <CustomizedSwitch
                                    disabled={cms1500Disabled}
                                    checked={cms1500Options.useStaffTaxonomyCode}
                                    name="useStaffTaxonomyCode"
                                    handleClick={() =>
                                        setCms1500Options((prev) => ({
                                            ...prev,
                                            useStaffTaxonomyCode: !prev.useStaffTaxonomyCode,
                                        }))
                                    }
                                />
                            </div>
                        </SimpleTooltip>
                        <div className="billing-settings-switch-label">
                            <p>Use Staff Taxonomy Code</p>
                            <span className="billing-settings-info-text">
                                When enabled, the rendering provider's taxonomy code will be included in each service line on claims (Box 24J)
                            </span>
                        </div>
                    </div>

                    <div className="billing-settings-switch-row" style={{ marginTop: 16 }}>
                        <SimpleTooltip title="Click Edit to make changes" placement="top" arrow>
                            <div
                                className={`billing-settings-switch-box${cms1500Disabled ? ' billing-settings-switch-box--disabled' : ''}`}>
                                <CustomizedSwitch
                                    disabled={cms1500Disabled}
                                    checked={cms1500Options.useStaffNpi}
                                    name="useStaffNpi"
                                    handleClick={() =>
                                        setCms1500Options((prev) => ({
                                            ...prev,
                                            useStaffNpi: !prev.useStaffNpi,
                                        }))
                                    }
                                />
                            </div>
                        </SimpleTooltip>
                        <div className="billing-settings-switch-label">
                            <p>Use Staff NPI</p>
                            <span className="billing-settings-info-text">
                                Each line uses its staff's NPI (Box 24J), or the authorization's if missing. Turns off Merge Appointments.
                            </span>
                        </div>
                    </div>

                    <div className="billing-settings-tool-info">
                        <img src={Images.toolInfoIcon} alt={'icon'} />
                        <p>These settings control how the CMS 1500 claim form is populated for this payer</p>
                    </div>
                </div>

                <div className="billing-settings-actions">
                    {showCms1500Edit && (
                        <CreateChancel
                            loader={!!cms1500Loader?.length}
                            create={'Save'}
                            chancel={'Cancel'}
                            onCreate={handleCms1500Submit}
                            onClose={handleCms1500Cancel}
                            buttonWidth="292px"
                            id={'cms1500-save'}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
